import { CascadiaJsonFormContext } from "@certn/forms";
import { useTranslation } from "react-i18next";
import { ActionListControl } from "@certn/forms/src/controls/ActionListControl";
import { getScopeLookupPath } from "@certn/forms/src/controls/utils";
import { ArrayControlProps } from "@jsonforms/core";
import get from "lodash/get";
import { useContext, useMemo } from "react";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { updateSection, removeItems, replaceOrAddItems } from "../../utils";
import { IdentityDocumentFilesRequirementsList } from "./IdentityDocumentFilesRequirementsList";
import { formatDocumentType } from "./utils";

const DATA_SECTION = ["identity_document_files", "document_files"];
const DATA_SECTION_ROOT = DATA_SECTION[0];

export function IdentityDocumentFilesControl(props: ArrayControlProps) {
  const { t } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { currentSection, basePath } = useApplication();
  const { isSubmitted } = useContext(CascadiaJsonFormContext);

  const rootSchemaWithoutRequirements = useMemo(() => {
    const scopePath = getScopeLookupPath(props.uischema?.scope ?? "");
    const fieldSchema = get(props.rootSchema, scopePath) as
      | { requirements?: string[] }
      | undefined;
    if (!fieldSchema?.requirements) return props.rootSchema;
    return {
      ...props.rootSchema,
      properties: {
        ...(props.rootSchema?.properties ?? {}),
        [DATA_SECTION_ROOT]: { ...fieldSchema, requirements: undefined },
      },
    };
  }, [props.rootSchema, props.uischema?.scope]);

  const errorsArray = useMemo(() => {
    const e = props.errors;
    if (Array.isArray(e)) return e;
    return typeof e === "string" && e ? e.split("\n").filter(Boolean) : [];
  }, [props.errors]);

  return (
    <>
      <ActionListControl
        {...props}
        rootSchema={rootSchemaWithoutRequirements as typeof props.rootSchema}
        hideFieldErrors
        renderTitle={(item: { document_type?: string }) =>
          item?.document_type
            ? formatDocumentType(item.document_type)
            : t("Identity document")
        }
        onItemChanged={async (data, values, index) => {
          const updatedArray = replaceOrAddItems(
            DATA_SECTION,
            data,
            values,
            index
          );
          if (caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          }
        }}
        onItemRemoved={async (parentValues, indexes) => {
          const updatedArray = removeItems(DATA_SECTION, parentValues, indexes);
          if (updatedArray && caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          }
        }}
      />
      <IdentityDocumentFilesRequirementsList
        rootSchema={props.rootSchema as Record<string, unknown>}
        uischemaScope={props.uischema?.scope ?? ""}
        data={props.data}
        errors={errorsArray}
        isSubmitted={isSubmitted}
        label={props.label ?? ""}
      />
    </>
  );
}
